<template>
  <div class="visor-cad2-container" :class="{ 'visor-cad2-fullscreen': isFullscreen }">
    <div v-if="cargando" class="visor-cad2-loading">
      <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem;">
        <span class="visually-hidden">Cargando visor CAD...</span>
      </div>
      <p class="mt-3 text-muted">Cargando visor CAD, por favor espere...</p>
    </div>
    <iframe
      ref="cadIframe"
      :src="iframeSrc"
      class="visor-cad2-iframe"
      frameborder="0"
      allowfullscreen
      @load="onIframeLoad"
      @error="onIframeError"
    ></iframe>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'

export default {
  name: 'VisorCAD2',
  props: {
    base64: {
      type: String,
      default: ''
    },
    isFullscreen: {
      type: Boolean,
      default: false
    },
    viewerUrl: {
      type: String,
      default: () => {
        // Usar window.location.origin para construir la URL del visor externo
        return `${window.location.origin}/cad-viewer/index.html`
      }
    }
  },
  emits: ['error'],
  setup(props, { emit }) {
    const cadIframe = ref(null)
    const cargando = ref(true)
    const iframeReady = ref(false)
    const timeoutId = ref(null)

    // Computed para iframeSrc (para evitar error de ESLint vue/no-setup-props-destructure)
    const iframeSrc = computed(() => {
      return props.viewerUrl || `${window.location.origin}/cad-viewer/index.html`
    })

    // Función para convertir base64 a ArrayBuffer
    const base64ToArrayBuffer = (base64) => {
      const binaryString = atob(base64)
      const bytes = new Uint8Array(binaryString.length)
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }
      return bytes.buffer
    }

    // Manejar mensajes del iframe
    const handleMessage = (event) => {
      // Verificar origen (permitir localhost con o sin puerto)
      const allowedOrigins = [
        'http://localhost',
        'http://localhost:8080',
        window.location.origin
      ]
      
      const isAllowedOrigin = allowedOrigins.some(origin => 
        event.origin.startsWith(origin)
      )

      if (!isAllowedOrigin) {
        console.warn('VisorCAD2: Mensaje de origen no permitido:', event.origin)
        return
      }

      if (!event.data || typeof event.data !== 'object' || !event.data.type) {
        console.warn('VisorCAD2: Mensaje sin tipo válido:', event.data)
        return
      }

      console.log('VisorCAD2: Mensaje recibido:', {
        type: event.data.type,
        origin: event.origin
      })

      switch (event.data.type) {
        case 'IFRAME_READY':
          console.log('VisorCAD2: Iframe listo para recibir archivos')
          iframeReady.value = true
          cargando.value = false
          
          // Limpiar timeout si existe
          if (timeoutId.value) {
            clearTimeout(timeoutId.value)
            timeoutId.value = null
          }

          // Si ya hay base64, cargarlo inmediatamente
          if (props.base64) {
            cargarArchivo(props.base64)
          }
          break

        case 'FILE_LOADED':
          console.log('VisorCAD2: Archivo cargado en iframe:', event.data.fileName)
          cargando.value = false
          break

        case 'FILE_LOAD_ERROR':
          console.error('VisorCAD2: Error en iframe:', event.data.error)
          cargando.value = false
          emit('error', event.data.error || 'Error al cargar el archivo en el visor CAD')
          break

        default:
          console.log('VisorCAD2: Mensaje desconocido del iframe:', event.data.type, event.data)
          // Verificar si es un mensaje de ping/pong
          if (event.data.type === 'PONG') {
            console.log('VisorCAD2: La comunicación funciona, pero el tipo de mensaje no coincide')
            console.log('VisorCAD2: Tipos esperados: IFRAME_READY, FILE_LOADED, FILE_LOAD_ERROR')
            console.log('VisorCAD2: Tipo recibido:', event.data.type)
          }
          break
      }
    }

    // Cargar archivo en el iframe
    const cargarArchivo = (base64Data) => {
      if (!iframeReady.value || !cadIframe.value || !cadIframe.value.contentWindow) {
        console.warn('VisorCAD2: Iframe no está listo aún')
        return
      }

      try {
        // Intentar convertir a ArrayBuffer primero (preferido)
        let arrayBuffer = null
        try {
          arrayBuffer = base64ToArrayBuffer(base64Data)
          cadIframe.value.contentWindow.postMessage({
            type: 'LOAD_FILE',
            payload: {
              arrayBuffer: arrayBuffer,
              fileName: 'archivo.dwg'
            }
          }, '*')
          console.log('VisorCAD2: Archivo enviado al iframe como ArrayBuffer')
        } catch (arrayBufferError) {
          console.warn('VisorCAD2: Error al convertir a ArrayBuffer, usando base64:', arrayBufferError)
          // Fallback a base64
          cadIframe.value.contentWindow.postMessage({
            type: 'LOAD_FILE',
            payload: {
              base64: base64Data,
              fileName: 'archivo.dwg'
            }
          }, '*')
          console.log('VisorCAD2: Archivo enviado al iframe como base64')
        }
      } catch (error) {
        console.error('VisorCAD2: Error al cargar archivo:', error)
        emit('error', 'Error al enviar el archivo al visor CAD')
      }
    }

    // Manejar carga del iframe
    const onIframeLoad = () => {
      console.log('VisorCAD2: Iframe cargado físicamente, esperando mensaje IFRAME_READY...')
      console.log('VisorCAD2: URL del iframe:', props.viewerUrl)

      // Verificar acceso al iframe (puede fallar por CORS/X-Frame-Options)
      try {
        if (cadIframe.value && cadIframe.value.contentWindow) {
          console.log('VisorCAD2: Iframe tiene contentWindow, verificando acceso...')
          
          // Intentar enviar un mensaje de prueba
          try {
            cadIframe.value.contentWindow.postMessage({ type: 'PING' }, '*')
            console.log('VisorCAD2: Intentando enviar mensaje de prueba al iframe...')
            setTimeout(() => {
              console.log('VisorCAD2: Mensaje de prueba enviado')
            }, 100)
          } catch (e) {
            console.error('VisorCAD2: Error al enviar mensaje de prueba:', e)
          }
        }
      } catch (error) {
        console.log('VisorCAD2: No se puede acceder al iframe (posible CORS/X-Frame-Options):', error.message)
      }

      // Timeout para detectar si el iframe no responde
      timeoutId.value = setTimeout(() => {
        if (!iframeReady.value) {
          console.error('VisorCAD2: El iframe no respondió después de 5 segundos.')
          console.error('VisorCAD2: URL intentada:', props.viewerUrl)
          console.error('VisorCAD2: Origen actual:', window.location.origin)
          console.error('VisorCAD2: Verifica que:')
          console.error('  1. El servidor en ' + props.viewerUrl + ' esté corriendo')
          console.error('  2. El servidor permita iframes (X-Frame-Options)')
          console.error('  3. El proyecto cad-viewer esté enviando el mensaje IFRAME_READY')
          emit('error', `No se pudo comunicar con el visor CAD desde ${props.viewerUrl}. Verifica que el servidor esté corriendo y que el proyecto cad-viewer esté enviando el mensaje IFRAME_READY.`)
          cargando.value = false
        }
      }, 5000)
    }

    // Manejar errores del iframe
    const onIframeError = () => {
      console.error('VisorCAD2: Error al cargar el iframe')
      cargando.value = false
      emit('error', 'Error al cargar el visor CAD. Verifica que el servidor esté corriendo.')
    }

    // Watch para cambios en base64
    watch(() => props.base64, (newBase64) => {
      if (newBase64 && iframeReady.value) {
        cargarArchivo(newBase64)
      }
    })

    // Escuchar mensajes del iframe
    onMounted(() => {
      window.addEventListener('message', handleMessage)
    })

    // Limpiar listeners al desmontar
    onBeforeUnmount(() => {
      window.removeEventListener('message', handleMessage)
      if (timeoutId.value) {
        clearTimeout(timeoutId.value)
      }
    })

    return {
      cadIframe,
      cargando,
      iframeSrc,
      // Estas funciones se usan en el template pero ESLint no las detecta
      onIframeLoad,
      onIframeError
    }
  }
}
</script>

<style scoped>
.visor-cad2-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.visor-cad2-iframe {
  width: 100%;
  height: 100%;
  border: none;
  min-height: 400px;
}

.visor-cad2-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 10;
}

.visor-cad2-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  background: white;
}

.visor-cad2-fullscreen .visor-cad2-iframe {
  height: 100vh;
}
</style>
